import { lazy, Suspense, useEffect, useState } from 'react';
import { Redirect, Route, Switch } from 'wouter';

import { Providers } from '@app/providers';
import { Router } from '@app/router';
import {
  RequireAuthWouter,
  SessionRevokedGuard,
  getUserPublicId,
  getUserRoles,
  hasAuthToken,
  startTokenRefreshScheduler,
} from '@modules/auth';
import { usersApi } from '@services';
import { endSession, setUserProfile, getUserProfile } from '@services/storage';
import { LoadingOverlay } from '@modules/landing';
import { ErrorBoundary, LoadingScreen, NotFoundScreen, SandboxNotice } from '@shared/ui/layout';
import { USER_ROLE_CLAIMS } from '@shared/constants';
import { brand } from '@shared/brand';
import type { UserRole } from '@shared/types';
import { PORTALS, PortalScope, RequireAccess, type PortalConfig } from '@modules/shell';
import { homePathForAccessRole, useMeAccess } from '@services/access';
import { environment } from '@shared/config';
import { getI18nService, I18nProvider } from '@infra/i18n';
import { TooltipProvider } from '@shared/ui';
import Landing from '@/pages/landing';
import Forbidden from '@/pages/forbidden';
import { BOOT_SPLASH_ENABLED, BOOT_SPLASH_DURATION_MS, PROFILE_GATE_TIMEOUT_MS } from './app/constants/app-boot';

const BootSplash = brand.components.BootSplash ?? LoadingOverlay;

function getBootSplashCopy() {
  // `common` is bundled eagerly and main.tsx awaits i18n init before rendering, so this
  // resolves for real. The defaultValue only covers the path where every init attempt
  // failed — there `t()` returns it instead of a raw key.
  return {
    title: brand.appName,
    titleHighlight: brand.bootTitleHighlight,
    message: getI18nService().t('common:boot.message', { defaultValue: 'Getting ready...' }),
  };
}

const LoginCallback = lazy(() =>
  import('@modules/auth').then((module) => ({
    default: module.LoginCallback,
  }))
);

const LogoutCallback = lazy(() =>
  import('@modules/auth').then((module) => ({
    default: module.LogoutCallback,
  }))
);

const SignUp = lazy(() =>
  import('@modules/auth').then((module) => ({
    default: module.SignUp,
  }))
);

const ForgotPassword = lazy(() =>
  import('@modules/auth').then((module) => ({
    default: module.ForgotPassword,
  }))
);

const ResetPassword = lazy(() =>
  import('@modules/auth').then((module) => ({
    default: module.ResetPassword,
  }))
);

function AuthedHomeRedirect() {
  const roles = getUserRoles();
  if (roles.includes(USER_ROLE_CLAIMS.ADMIN)) {
    return <Redirect href={homePathForAccessRole('super_admin')} />;
  }
  return (
    <Providers>
      <UserAccessHomeRedirect />
    </Providers>
  );
}

function UserAccessHomeRedirect() {
  const { role, isLoading } = useMeAccess();
  if (isLoading) return <LoadingScreen />;
  return <Redirect href={homePathForAccessRole(role)} />;
}

const JWT_CLAIM_FOR_LAYOUT_ROLE = {
  ADMIN: USER_ROLE_CLAIMS.ADMIN,
  USER: USER_ROLE_CLAIMS.USER,
} as const satisfies Record<UserRole, string>;

/** One portal's route tree: JWT gate, profile gate, then the portal's access capability. */
function PortalBranch({ portal, profileReady }: { portal: PortalConfig; profileReady: boolean }) {
  return (
    <RequireAuthWouter roles={[JWT_CLAIM_FOR_LAYOUT_ROLE[portal.requiredRole]]}>
      {/* Cold-load gate: hold the authed app until the full profile is loaded (scoped
          here so public routes are never blocked). */}
      {profileReady ? (
        <Providers>
          <PortalScope portal={portal}>
            {portal.capability ? (
              <RequireAccess capability={portal.capability}>
                <Router />
              </RequireAccess>
            ) : (
              <Router />
            )}
          </PortalScope>
        </Providers>
      ) : (
        <LoadingScreen />
      )}
    </RequireAuthWouter>
  );
}

function App() {
  const authed = hasAuthToken();
  const roles = getUserRoles();
  const isAdmin = roles.includes(USER_ROLE_CLAIMS.ADMIN);
  const isUser = roles.includes(USER_ROLE_CLAIMS.USER);
  const hasKnownRole = isAdmin || isUser;
  const [showBootSplash, setShowBootSplash] = useState(false);
  // Gate the boot overlay on the full profile being loaded (cold loads only). Starts open when
  // a profile is already cached (login seed / persisted F5), so normal loads are unaffected.
  const [profileReady, setProfileReady] = useState<boolean>(() => !!getUserProfile());
  const bootSplashCopy = getBootSplashCopy();

  useEffect(() => {
    // If we have a token stored but no recognized roles, treat it as invalid/stale and clear it.
    // This prevents users from being stuck on /forbidden when they simply need to re-login.
    if (authed && !hasKnownRole) {
      // A local-only clear leaves the SSO cookie alive: pressing Login would silently
      // re-mint the same unknown-role token and bounce back to /forbidden in a loop.
      // Ending the session via FusionAuth breaks that loop — after the /logout-callback
      // round trip there is no token left to re-mint from.
      void endSession('logout');
      return;
    }

    // Start proactive refresh scheduler for already-authenticated users (e.g. page reload)
    if (authed && hasKnownRole) {
      startTokenRefreshScheduler();

      // The profile snapshot isn't in the JWT. On a fresh load / F5 the LoginCallback seed
      // doesn't run, so seed it once here (if empty). We hold the boot overlay up until it
      // resolves so the app never appears with an incomplete user (e.g. gender for the AI
      // roleplay agent_info). A timeout caps the wait so a slow/failed profile API can't freeze
      // startup — the overlay lifts anyway and lazy seeding takes over.
      const userPublicId = getUserPublicId();
      if (userPublicId && !getUserProfile()) {
        let done = false;
        const openGate = () => {
          if (!done) {
            done = true;
            setProfileReady(true);
          }
        };
        const timer = window.setTimeout(openGate, PROFILE_GATE_TIMEOUT_MS);
        usersApi
          .getProfile(userPublicId)
          .then((profile) => {
            // Skip if auth changed while the fetch was in flight (logout / account switch) —
            // otherwise a late-resolving fetch could resurrect a stale profile after logout.
            if (getUserPublicId() === userPublicId) setUserProfile(profile);
          })
          .catch(() => {
            // Non-critical — the snapshot simply won't be seeded until a profile load runs.
          })
          .finally(() => {
            window.clearTimeout(timer);
            openGate();
          });
        return () => window.clearTimeout(timer);
      }

      // Already cached (or nothing to fetch) — open the gate immediately.
      setProfileReady(true);
    }
  }, [authed, hasKnownRole]);

  useEffect(() => {
    let timer: number | undefined;
    if (BOOT_SPLASH_ENABLED) {
      setShowBootSplash(true);
      timer = window.setTimeout(() => {
        setShowBootSplash(false);
      }, BOOT_SPLASH_DURATION_MS);
    }
    return () => {
      if (timer) window.clearTimeout(timer);
    };
  }, []);

  return (
    <ErrorBoundary>
      <BootSplash isLoading={showBootSplash} copy={bootSplashCopy} />
      {/* Single-session: "logged in elsewhere" modal (WS + 401 fallback). */}
      <I18nProvider>
        <SessionRevokedGuard />
      </I18nProvider>
      <Switch>
        <Route path="/">
          {authed && hasKnownRole ? (
            <AuthedHomeRedirect />
          ) : (
            <I18nProvider>
              <TooltipProvider delayDuration={200}>
                <SandboxNotice />
                <Landing />
              </TooltipProvider>
            </I18nProvider>
          )}
        </Route>

        <Route path="/login-callback">
          <Suspense fallback={<LoadingScreen />}>
            <LoginCallback />
          </Suspense>
        </Route>

        <Route path="/logout-callback">
          <Suspense fallback={<LoadingScreen />}>
            <LogoutCallback />
          </Suspense>
        </Route>

        <Route path="/register">
          {!environment.registrationEnabled ? (
            <Redirect href="/" />
          ) : authed && hasKnownRole ? (
            <AuthedHomeRedirect />
          ) : (
            <I18nProvider>
              <TooltipProvider delayDuration={200}>
                <Suspense fallback={<LoadingScreen />}>
                  <SignUp />
                </Suspense>
              </TooltipProvider>
            </I18nProvider>
          )}
        </Route>

        {/* Public: reachable signed in or not (the link also comes from the FusionAuth login page). */}
        <Route path="/forgot-password">
          <Providers>
            <Suspense fallback={<LoadingScreen />}>
              <ForgotPassword />
            </Suspense>
          </Providers>
        </Route>

        <Route path="/reset-password">
          <Providers>
            <Suspense fallback={<LoadingScreen />}>
              <ResetPassword />
            </Suspense>
          </Providers>
        </Route>

        <Route path="/403" component={Forbidden} />
        <Route path="/forbidden">
          <Redirect href="/403" />
        </Route>

        <Route path={`${PORTALS.admin.basePath}/*`}>
          <PortalBranch portal={PORTALS.admin} profileReady={profileReady} />
        </Route>

        <Route path={`${PORTALS.user.basePath}/*`}>
          <PortalBranch portal={PORTALS.user} profileReady={profileReady} />
        </Route>

        <Route path={`${PORTALS.org.basePath}/*`}>
          <PortalBranch portal={PORTALS.org} profileReady={profileReady} />
        </Route>

        <Route path="/404">
          <I18nProvider>
            <NotFoundScreen />
          </I18nProvider>
        </Route>
        <Route>
          <Redirect href="/404" />
        </Route>
      </Switch>
    </ErrorBoundary>
  );
}

export default App;
